import React, { useEffect, useState } from 'react';
import styles from '../../assets/stylesheets/AnnouncementPopup.module.scss';
import useUserCredentials from '../../utils/hooks/user/useUserCredentials';
import { UserRole } from '../../utils/api/endpoints/user/credentials';
import { useNavigate } from 'react-router-dom';
import { FiShield, FiLock, FiUsers, FiLink } from 'react-icons/fi';

// Announcement types for different content
type AnnouncementType = 'pentest_reports' | 'credential_vault';

interface Announcement {
  key: string;
  type: AnnouncementType;
  launchDate: Date;
  expiryDate: Date; // Stop showing after this date
  roles: UserRole[]; // Which roles can see this announcement
}

// To support future announcements, use a versioned key and launch/expiry dates
const ANNOUNCEMENTS: Announcement[] = [
  {
    key: 'announcement_20250719_dismissed',
    type: 'pentest_reports',
    launchDate: new Date('2025-07-19T00:00:00Z'),
    expiryDate: new Date('2025-08-19T00:00:00Z'), // 30 days from launch
    roles: [UserRole.BUSINESS],
  },
  {
    key: 'announcement_credential_vault_beta',
    type: 'credential_vault',
    launchDate: new Date('2025-12-16T00:00:00Z'),
    expiryDate: new Date('2026-01-16T23:59:59Z'), // Expires Jan 16, 2026
    roles: [UserRole.BUSINESS],
  },
  // Add future announcements here
];

function getActiveAnnouncement(role: UserRole | undefined) {
  if (!role) return null;
  const now = new Date();
  return ANNOUNCEMENTS.find(a => {
    const isAfterLaunch = now >= a.launchDate;
    const isBeforeExpiry = now <= a.expiryDate;
    const isForRole = a.roles.includes(role);
    const notDismissed = !localStorage.getItem(a.key);
    return isAfterLaunch && isBeforeExpiry && isForRole && notDismissed;
  }) || null;
}

const AnnouncementPopup: React.FC = () => {
  const { role } = useUserCredentials();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeAnnouncement, setActiveAnnouncement] = useState<Announcement | null>(null);

  useEffect(() => {
    const announcement = getActiveAnnouncement(role);
    if (announcement) {
      setActiveAnnouncement(announcement);
      setVisible(true);
      setTimeout(() => setIsAnimating(true), 100);
    }
  }, [role]);

  const handleDismiss = () => {
    if (activeAnnouncement) {
      localStorage.setItem(activeAnnouncement.key, 'true');
    }
    setIsAnimating(false);
    setTimeout(() => setVisible(false), 300);
  };

  const handlePrimaryAction = () => {
    if (activeAnnouncement?.type === 'credential_vault') {
      navigate('/dashboard/vault');
    } else if (activeAnnouncement?.type === 'pentest_reports') {
      navigate('/dashboard/pentest-reports');
    }
    handleDismiss();
  };

  if (!visible || !activeAnnouncement) return null;

  // Render Credential Vault announcement
  if (activeAnnouncement.type === 'credential_vault') {
    return (
      <>
        <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm" onClick={handleDismiss} />
        <div className={`fixed inset-0 z-[10000] flex items-center justify-center pointer-events-none`}>
          <div className={`bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden pointer-events-auto transform transition-all duration-300 ${isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
            {/* Header with gradient */}
            <div className="relative px-6 py-8 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 text-center overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute top-0 left-0 w-32 h-32 bg-green-500/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-green-500/10 rounded-full translate-x-1/2 translate-y-1/2" />

              <button
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white transition-colors"
                onClick={handleDismiss}
                aria-label="Close announcement"
              >
                ×
              </button>

              <div className="relative">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl shadow-lg mb-4">
                  <FiShield className="w-8 h-8 text-white" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h2 className="text-2xl font-bold text-white">Credential Vault</h2>
                  <span className="px-2 py-0.5 text-xs font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full uppercase tracking-wide">
                    Beta
                  </span>
                </div>
                <p className="text-slate-300 text-sm">Introducing our new secure credential management system</p>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                    <FiLock className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Zero-Knowledge Encryption</h4>
                    <p className="text-sm text-gray-600">Your credentials are encrypted with AES-256-GCM. We never see your data.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                    <FiUsers className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Team Collaboration</h4>
                    <p className="text-sm text-gray-600">Share credentials securely with your team members with granular permissions.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                    <FiLink className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Program Integration</h4>
                    <p className="text-sm text-gray-600">Link credentials to programs and share with pentesters via access passwords.</p>
                  </div>
                </div>
              </div>

              {/* Notice */}
              <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">Beta Notice:</span> This feature is in beta. We'd love your feedback to help us improve it.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-between items-center">
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={handlePrimaryAction}
                className="px-6 py-2.5 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                Get Started
                <span>→</span>
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Render Pentest Reports announcement (existing)
  return (
    <>
      <div className={styles.overlay} onClick={handleDismiss} />
      <div className={`${styles.announcementPopup} ${isAnimating ? styles.animate : ''}`}>
        <div className={styles.header}>
          <div className={styles.badge}>
            <span className={styles.badgeIcon}>✨</span>
            <span className={styles.badgeText}>NEW FEATURE</span>
          </div>
          <button
            className={styles.closeBtn}
            onClick={handleDismiss}
            aria-label="Close announcement"
          >
            ×
          </button>
        </div>
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <div className={styles.mainIcon}>🚀</div>
          </div>
          <h2 className={styles.title}>
          Reporting Made Easy
          </h2>
          <p className={styles.subtitle}>
          You can now generate pentest reports directly from the platform!
          No exports. No delays. Just fast, expert-reviewed reporting.
          </p>
          <div className={styles.features}>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>⚡</span>
              <span>Auto-generated reports</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>👥</span>
              <span>Reviewed by our pentesters</span>
            </div>
            <div className={styles.feature}>
              <span className={styles.featureIcon}>💬</span>
              <span>Real-time chat for edits</span>
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          <button
            className={styles.primaryBtn}
            onClick={handlePrimaryAction}
          >
            Request Report
            <span className={styles.btnIcon}>→</span>
          </button>
          <button
            className={styles.secondaryBtn}
            onClick={handleDismiss}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </>
  );
};

export default AnnouncementPopup;